/**
 * Super Inteligente Checkout - Premium UX
 * Preview stores, flat rate + extra store charge, delivery instructions, saved addresses
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Truck, Clock, ShoppingCart, ChevronLeft, Package, ArrowRight, Store, RefreshCw, X, Check, Navigation, Bike, Car, CreditCard, Banknote, Shield, Heart, Users, Home } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { MarketplaceHeader, MarketplaceFooter, MobileBottomNav } from '../../components/marketplace/MarketplaceLayout';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api';
import { toast } from 'sonner';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function MapClickHandler({ onSelect }) {
  useMapEvents({ click: (e) => onSelect({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
}
function FlyTo({ coords }) {
  const map = useMap();
  useEffect(() => { if (coords) map.flyTo([coords.lat, coords.lng], 16, { duration: 1 }); }, [coords]);
  return null;
}

const SuperCheckout = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [form, setForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    delivery_address: '', delivery_references: '',
    payment_method: 'cash_on_delivery',
    delivery_if_absent: 'wait',
    neighbor_instructions: '',
  });
  const [coords, setCoords] = useState(null);
  const [substitutionPref, setSubstitutionPref] = useState('substitute');
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [locatingUser, setLocatingUser] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('super_cart');
    if (saved) {
      const items = JSON.parse(saved);
      if (items.length === 0) { navigate('/super'); return; }
      setCart(items);
    } else { navigate('/super'); }
    loadSavedAddresses();
  }, []);

  // Run preview when coords change (algorithm needs location to find stores)
  useEffect(() => {
    if (coords && cart.length > 0) runPreview();
  }, [coords]);

  const loadSavedAddresses = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    try {
      const res = await api.get('/super-inteligente/saved-addresses', { params: { customer_token: token } });
      setSavedAddresses(res.data.addresses || []);
      if (res.data.customer) {
        setForm(f => ({ ...f, customer_name: f.customer_name || res.data.customer.name || '', customer_phone: f.customer_phone || res.data.customer.phone || '', customer_email: f.customer_email || res.data.customer.email || '' }));
      }
    } catch (e) {}
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await api.post('/super-inteligente/preview', {
        items: cart.map(i => ({ catalog_product_id: i.catalog_product_id, quantity: i.quantity })),
        delivery_location: coords,
      });
      setPreview(res.data);
    } catch (e) { console.error(e); }
    finally { setPreviewLoading(false); }
  };

  const subtotal = cart.reduce((s, i) => s + i.unified_price * i.quantity, 0);
  const deliveryTotal = preview?.total_delivery || 58;
  const total = subtotal + deliveryTotal;

  const handleLocationSelect = (loc) => {
    setCoords(loc);
    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${loc.lat}&lon=${loc.lng}&zoom=18`)
      .then(r => r.json())
      .then(data => { if (data.display_name) setForm(f => ({ ...f, delivery_address: data.display_name.split(',').slice(0, 3).join(', ') })); })
      .catch(() => {});
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocalización no disponible'); return; }
    setLocatingUser(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { handleLocationSelect({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocatingUser(false); },
      () => { toast.error('No se pudo obtener tu ubicación'); setLocatingUser(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const selectSavedAddress = (addr) => {
    if (addr.location?.lat) setCoords(addr.location);
    setForm(f => ({ ...f, delivery_address: addr.address || '' }));
    toast.success('Dirección seleccionada');
  };

  const handleSubmit = async () => {
    if (!form.customer_name || !form.customer_phone) { toast.error('Ingresa tu nombre y teléfono'); return; }
    if (!coords) { toast.error('Selecciona tu ubicación en el mapa'); return; }
    if (!form.delivery_address) { toast.error('Ingresa tu dirección'); return; }
    if (form.delivery_if_absent === 'neighbor' && !form.neighbor_instructions) { toast.error('Indica con quién dejar el pedido'); return; }

    setSubmitting(true);
    try {
      const res = await api.post('/super-inteligente/orders', {
        items: cart.map(i => ({ catalog_product_id: i.catalog_product_id, quantity: i.quantity })),
        customer_name: form.customer_name, customer_phone: form.customer_phone,
        customer_email: form.customer_email || undefined,
        delivery_address: form.delivery_address, delivery_location: coords,
        delivery_references: form.delivery_references || undefined,
        delivery_if_absent: form.delivery_if_absent,
        neighbor_instructions: form.delivery_if_absent === 'neighbor' ? form.neighbor_instructions : undefined,
        payment_method: form.payment_method,
        substitution_preference: substitutionPref,
      });
      localStorage.removeItem('super_cart');
      localStorage.setItem('super_active_order', res.data.order.id);
      toast.success('¡Pedido creado!');
      navigate(`/super/pedido/${res.data.order.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear pedido');
    } finally { setSubmitting(false); }
  };

  const up = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div className="min-h-screen bg-gray-50">
      <MarketplaceHeader />

      <Helmet>
        <title>Confirmar Pedido | Super Inteligente - enLocal</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="bg-[#0a1628] text-white py-5 px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate('/super')} className="p-2 hover:bg-white/10 rounded-xl transition"><ChevronLeft className="w-5 h-5" /></button>
          <div>
            <h1 className="text-lg font-extrabold">Confirmar pedido</h1>
            <p className="text-sm text-white/50">{cart.length} productos · ${subtotal.toFixed(2)}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4 pb-44">

        {/* 1. Dirección */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-emerald-600" /> ¿Dónde te entregamos?
          </h2>

          {savedAddresses.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-2">Direcciones guardadas:</p>
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {savedAddresses.map((addr, i) => (
                  <button key={addr.id || i} onClick={() => selectSavedAddress(addr)}
                    className="flex-shrink-0 flex items-center gap-2 bg-gray-50 hover:bg-emerald-50 border border-gray-200 hover:border-emerald-400 rounded-xl px-3 py-2 text-sm transition">
                    <MapPin className="w-3.5 h-3.5 text-gray-400" />
                    <span className="max-w-[180px] truncate text-gray-700">{addr.label || addr.address}</span>
                    {addr.is_default && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 rounded font-medium">Principal</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="h-44 rounded-xl overflow-hidden border border-gray-200">
            <MapContainer center={coords || [20.6534, -105.2253]} zoom={14} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <MapClickHandler onSelect={handleLocationSelect} />
              <FlyTo coords={coords} />
              {coords && <Marker position={[coords.lat, coords.lng]} />}
            </MapContainer>
          </div>

          <button onClick={useCurrentLocation} disabled={locatingUser}
            className="w-full flex items-center justify-center gap-2 mt-3 py-3 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-700 font-medium text-sm transition disabled:opacity-50">
            <Navigation className={`w-4 h-4 ${locatingUser ? 'animate-pulse' : ''}`} />
            {locatingUser ? 'Obteniendo ubicación...' : 'Usar mi ubicación actual'}
          </button>

          <input type="text" placeholder="Dirección completa *" value={form.delivery_address}
            onChange={e => up('delivery_address', e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm mt-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
          <input type="text" placeholder="Referencias (entre calles, color de casa)" value={form.delivery_references}
            onChange={e => up('delivery_references', e.target.value)}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm mt-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
        </div>

        {/* 2. Datos (only show if not logged in or data incomplete) */}
        {(!form.customer_name || !form.customer_phone) ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <h2 className="font-bold text-gray-900 mb-3">Tus datos</h2>
            <div className="space-y-2">
              <input type="text" placeholder="Nombre completo *" value={form.customer_name} onChange={e => up('customer_name', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
              <input type="tel" placeholder="Teléfono (10 dígitos) *" value={form.customer_phone} onChange={e => up('customer_phone', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
              <input type="email" placeholder="Email (opcional)" value={form.customer_email} onChange={e => up('customer_email', e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">{form.customer_name}</p>
                <p className="text-xs text-gray-500">{form.customer_phone} {form.customer_email ? `· ${form.customer_email}` : ''}</p>
              </div>
            </div>
            <button onClick={() => up('customer_name', '')} className="text-xs text-emerald-600 font-medium">Editar</button>
          </div>
        )}

        {/* 3. Preview de tiendas + Envío */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Truck className="w-5 h-5 text-emerald-600" /> Envío por enLocal
          </h2>

          {previewLoading ? (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600 mr-3"></div>
              <span className="text-sm text-gray-500">Calculando mejor ruta...</span>
            </div>
          ) : preview ? (
            <>
              {/* Store count badge */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">🏪</div>
                  <div>
                    <p className="font-bold text-amber-900">
                      {preview.num_stores === 1 ? 'Tu pedido se recoge en 1 tienda' : `Tu pedido se recoge en ${preview.num_stores} tiendas`}
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      {preview.store_names.join(' → ')} → Tu domicilio
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 bg-amber-100/50 rounded-lg px-3 py-2">
                  <Heart className="w-4 h-4 text-amber-600" />
                  <p className="text-xs text-amber-800 font-medium">
                    Con este pedido apoyas a {preview.num_stores} {preview.num_stores === 1 ? 'tienda local' : 'tiendas locales'} de tu comunidad
                  </p>
                </div>
              </div>

              {/* Vehicle + cost */}
              <div className={`flex items-center gap-4 p-4 rounded-xl border-2 ${preview.vehicle_type === 'car' ? 'border-blue-200 bg-blue-50' : 'border-emerald-200 bg-emerald-50'}`}>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${preview.vehicle_type === 'car' ? 'bg-blue-200' : 'bg-emerald-200'}`}>
                  {preview.vehicle_type === 'car' ? <Car className="w-6 h-6 text-blue-700" /> : <Bike className="w-6 h-6 text-emerald-700" />}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-gray-900">Envío en {preview.vehicle_label}</p>
                  <p className="text-xs text-gray-600">~{preview.estimated_km} km de recorrido</p>
                </div>
                <p className="text-lg font-extrabold text-gray-900">${preview.vehicle_fee}</p>
              </div>

              {preview.num_stores > 1 && (
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                  <Store className="w-3.5 h-3.5" />
                  +${preview.extra_store_fee_unit} por cada tienda adicional ({preview.num_stores - 1} extra = ${preview.extra_stores_cost})
                </p>
              )}

              {preview.unavailable_count > 0 && (
                <p className="text-xs text-amber-600 mt-2">⚠️ {preview.unavailable_count} producto(s) no disponibles en tu zona</p>
              )}
            </>
          ) : (
            <div className="text-center py-6 text-gray-400">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Selecciona tu ubicación para calcular el envío</p>
            </div>
          )}

          <div className="flex items-center gap-2 mt-3 text-xs text-gray-400">
            <Clock className="w-3.5 h-3.5" />
            <span>Tiempo estimado: 25-40 minutos</span>
          </div>
        </div>

        {/* 4. Si no estás en casa */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
            <Home className="w-5 h-5 text-emerald-600" /> Si no estás en casa
          </h2>
          <p className="text-xs text-gray-500 mb-3">¿Qué hace el repartidor si no te encuentra?</p>
          <div className="space-y-2">
            {[
              { value: 'wait', label: 'Esperar en el domicilio', desc: 'El repartidor espera hasta 5 minutos', icon: '⏳' },
              { value: 'neighbor', label: 'Dejar con un vecino', desc: 'Indica con quién dejarlo', icon: '🏠' },
              { value: 'door', label: 'Dejar en la puerta', desc: 'Bajo tu responsabilidad', icon: '📦' },
            ].map(opt => (
              <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${form.delivery_if_absent === opt.value ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <input type="radio" name="absent" value={opt.value} checked={form.delivery_if_absent === opt.value}
                  onChange={() => {
                    up('delivery_if_absent', opt.value);
                    if (opt.value === 'neighbor' || opt.value === 'door') up('payment_method', 'transfer');
                  }} className="sr-only" />
                <span className="text-xl">{opt.icon}</span>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 text-sm">{opt.label}</p>
                  <p className="text-xs text-gray-500">{opt.desc}</p>
                </div>
                {form.delivery_if_absent === opt.value && <Check className="w-5 h-5 text-emerald-600" />}
              </label>
            ))}
          </div>
          {form.delivery_if_absent === 'neighbor' && (
            <input type="text" placeholder="Nombre del vecino e indicaciones (ej: Sr. López, casa azul a la derecha)" value={form.neighbor_instructions}
              onChange={e => up('neighbor_instructions', e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm mt-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
          )}
        </div>

        {/* 5. Sustitución */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-1 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-emerald-600" /> Si un producto no está
          </h2>
          <p className="text-xs text-gray-500 mb-3">¿Qué hacemos si no encontramos algo?</p>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setSubstitutionPref('substitute')}
              className={`p-3 rounded-xl text-sm font-medium transition border-2 text-center ${substitutionPref === 'substitute' ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'border-gray-200 text-gray-600'}`}>
              <RefreshCw className="w-5 h-5 mx-auto mb-1" />
              Buscar sustituto
            </button>
            <button onClick={() => setSubstitutionPref('cancel')}
              className={`p-3 rounded-xl text-sm font-medium transition border-2 text-center ${substitutionPref === 'cancel' ? 'bg-red-50 border-red-400 text-red-600' : 'border-gray-200 text-gray-600'}`}>
              <X className="w-5 h-5 mx-auto mb-1" />
              Cancelar y reembolsar
            </button>
          </div>
        </div>

        {/* 6. Pago */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-3">Método de pago</h2>
          {(form.delivery_if_absent === 'neighbor' || form.delivery_if_absent === 'door') && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4 text-purple-600 flex-shrink-0" />
              <p className="text-xs text-purple-700">Al dejar el pedido con un vecino o en la puerta, el pago debe ser por transferencia antes del envío.</p>
            </div>
          )}
          <div className="space-y-2">
            {[
              { value: 'cash_on_delivery', label: 'Efectivo al recibir', icon: <Banknote className="w-6 h-6 text-green-600" />, desc: 'Paga al repartidor' },
              { value: 'card_on_delivery', label: 'Tarjeta al recibir', icon: <CreditCard className="w-6 h-6 text-blue-600" />, desc: 'El repartidor lleva terminal' },
              { value: 'transfer', label: 'Transferencia bancaria', icon: <Shield className="w-6 h-6 text-purple-600" />, desc: 'Paga antes del envío · Ideal para dejar con vecino' },
            ].filter(m => {
              if (form.delivery_if_absent === 'neighbor' || form.delivery_if_absent === 'door') return m.value === 'transfer';
              return true;
            }).map(m => (
              <label key={m.value} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${form.payment_method === m.value ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'}`}>
                <input type="radio" name="payment" value={m.value} checked={form.payment_method === m.value}
                  onChange={() => up('payment_method', m.value)} className="sr-only" />
                {m.icon}
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{m.label}</p>
                  <p className="text-xs text-gray-500">{m.desc}</p>
                </div>
                {form.payment_method === m.value && <Check className="w-5 h-5 text-emerald-600" />}
              </label>
            ))}
          </div>
          {form.payment_method === 'transfer' && (
            <div className="mt-3 bg-purple-50 border border-purple-200 rounded-xl p-4">
              <p className="font-bold text-purple-900 text-sm mb-2">Datos para transferencia:</p>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-purple-700">Banco:</span>
                  <span className="font-medium text-purple-900">STP / enLocal</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-purple-700">CLABE:</span>
                  <span className="font-mono font-medium text-purple-900">646180204800000123</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-purple-700">Beneficiario:</span>
                  <span className="font-medium text-purple-900">Todo en Local SA de CV</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-purple-700">Monto exacto:</span>
                  <span className="font-bold text-purple-900">${total.toFixed(2)}</span>
                </div>
              </div>
              <p className="text-xs text-purple-600 mt-3">Tu pedido se despacha al confirmar el pago. Envía tu comprobante por WhatsApp al 322-158-2052 con tu número de pedido.</p>
            </div>
          )}
        </div>

        {/* 7. Resumen */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-emerald-600" /> Resumen
          </h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Productos ({cart.length})</span>
              <span className="font-medium">${subtotal.toFixed(2)}</span>
            </div>
            {preview && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-500">Envío ({preview.vehicle_label.toLowerCase()})</span>
                  <span className="font-medium">${preview.vehicle_fee.toFixed(2)}</span>
                </div>
                {preview.extra_stores_cost > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tiendas adicionales ({preview.num_stores - 1})</span>
                    <span className="font-medium">${preview.extra_stores_cost.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-400">
                  <span>Cargo por servicio</span>
                  <span>${preview.service_fee.toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-100">
              <span className="font-bold text-gray-900">Total</span>
              <span className="text-xl font-extrabold text-emerald-600">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-xs text-gray-400 py-1">
          <Shield className="w-3.5 h-3.5" />
          <span>Pago seguro · Tu información está protegida</span>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-40 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="max-w-2xl mx-auto">
          <button onClick={handleSubmit} disabled={submitting || !preview}
            className="w-full bg-emerald-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-emerald-700 transition shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? (
              <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Procesando...</>
            ) : (
              <>Pagar ${total.toFixed(2)} <ArrowRight className="w-5 h-5" /></>
            )}
          </button>
        </div>
      </div>

      <MarketplaceFooter />
      <MobileBottomNav />
    </div>
  );
};

export default SuperCheckout;
